import { Injectable } from '@angular/core';
import { map, Observable, shareReplay } from 'rxjs';
import { LayersStreams } from '../../common/current-layers/layers-streams.service';
import { ProjectStreamService } from '../../common/current-project/project-stream.service';
import { GeometryTypes } from '../../common/utility/geometry-utils';
import { Style } from '../styles/style';
import { FeatureService } from './feature.service';

export type JobLayerDetail = { id: string; name: string; geomType: GeometryTypes; style: Style; layerColor: string };

export type JobFeatureDetailType = {
    totalCount: number;
    layers: Array<JobLayerDetail & { count: number }>;
};

@Injectable({
    providedIn: 'root'
})
export class JobFeatureService {
    private layerIdMap: Map<string, JobLayerDetail> = new Map();

    constructor(
        private featureService: FeatureService,
        private projectStream: ProjectStreamService,
        private layerStream: LayersStreams
    ) {}

    /**
     * Returns the feature count for a job as an Observable, using cache if available, otherwise fetches from API and caches it.
     */
    getFeaturesDetail(jobId: string): Observable<JobFeatureDetailType> {
        const projectId = this.projectStream.getCurrentProject().id;
        const layers = this.layerStream.currentMapWorkspaceLayers;
        const layersSorted = this.layerStream.sortLayers(layers);
        if (this.layerIdMap.size === 0 && layers.length > 0) {
            layersSorted.forEach(layer => {
                const detail = {
                    id: layer.id,
                    name: layer.layerName,
                    geomType: layer.geometryType,
                    style: layer.style,
                    layerColor: layer.getLayerColor()
                };
                this.layerIdMap.set(layer.id, detail);
            });
        }
        const layerIds: string[] = Array.from(this.layerIdMap.keys());

        return this.featureService.getFeaturesDetailByJobId(projectId, jobId, layerIds).pipe(
            // Map the API response to JobIdToFeatureDetailType
            map((data: Record<string, any>) => {
                if (!data || !data.aggregations) {
                    return { totalCount: 0, layers: [] };
                }
                let layerDetail: Array<JobLayerDetail & { count: number }> = [];
                layersSorted.forEach(layer => {
                    if (data.aggregations[layer.id] > 0) {
                        const layerWithCount = {
                            ...this.layerIdMap.get(layer.id),
                            count: data.aggregations[layer.id]
                        };
                        layerDetail.push(layerWithCount);
                    }
                });
                const result: JobFeatureDetailType = {
                    totalCount: data.aggregations['count'],
                    layers: layerDetail
                };
                return result;
            }),
            shareReplay(1)
        );
    }

    clearCache(): void {
        this.layerIdMap.clear();
    }
}
